import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";

function WhatsAppIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="16" r="16" fill="#25D366" />
      <path fillRule="evenodd" clipRule="evenodd"
        d="M23.5 8.5C21.6 6.6 19.1 5.5 16.4 5.5C10.9 5.5 6.4 10 6.4 15.5C6.4 17.3 6.9 19.1 7.8 20.6L6.3 26L11.8 24.5C13.3 25.3 14.8 25.8 16.4 25.8C21.9 25.8 26.4 21.3 26.4 15.8C26.4 13.1 25.4 10.4 23.5 8.5ZM16.4 24C15 24 13.6 23.6 12.4 22.9L12.1 22.7L8.8 23.6L9.7 20.4L9.5 20.1C8.7 18.8 8.3 17.2 8.3 15.6C8.3 11 12.1 7.3 16.5 7.3C18.6 7.3 20.6 8.1 22.1 9.6C23.6 11.1 24.4 13.1 24.4 15.2C24.5 19.8 20.8 24 16.4 24ZM20.7 17.5C20.5 17.4 19.3 16.8 19.1 16.7C18.9 16.6 18.8 16.6 18.6 16.8C18.5 17 17.9 17.6 17.8 17.7C17.7 17.9 17.5 17.9 17.3 17.8C17.1 17.7 16.4 17.5 15.5 16.7C14.9 16.1 14.4 15.4 14.3 15.2C14.2 15 14.3 14.8 14.4 14.7C14.5 14.6 14.7 14.4 14.8 14.3C14.9 14.2 15 14.1 15 14C15.1 13.8 15 13.7 15 13.6C14.9 13.5 14.4 12.3 14.2 11.8C14 11.3 13.8 11.4 13.7 11.4C13.5 11.3 13.4 11.3 13.2 11.3C13.1 11.3 12.8 11.4 12.6 11.6C12.4 11.8 11.8 12.4 11.8 13.6C11.8 14.8 12.6 15.9 12.7 16.1C12.8 16.3 14.4 18.8 16.8 19.8C17.4 20.1 17.9 20.3 18.3 20.4C18.9 20.6 19.5 20.6 19.9 20.5C20.4 20.4 21.4 19.8 21.6 19.2C21.8 18.6 21.8 18.1 21.7 18C21.7 17.7 21 17.6 20.7 17.5Z"
        fill="white" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2 2L16 16M16 2L2 16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function WhatsAppWidget() {
  const [open, setOpen] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [customText, setCustomText] = useState("");
  const [sent, setSent] = useState(false);
  const [pulse, setPulse] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/whatsapp-faq").then(r => setFaqs(r.data || [])).catch(() => setFaqs([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setPulse(false), 4000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (selected === "custom" && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [selected]);

  const reset = () => { setSelected(null); setCustomText(""); setSent(false); };

  const handleClose = () => { setOpen(false); setTimeout(reset, 300); };

  const sendToWhatsApp = (text) => {
    if (!text.trim()) return;
    const phone = faqs.find(f => f._whatsapp_number)?._whatsapp_number || "";
    const encoded = encodeURIComponent(text.trim());
    const url = phone ? `https://wa.me/${phone.replace(/\D/g, "")}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
    setSent(true);
    setTimeout(() => window.open(url, "_blank"), 400);
    setTimeout(() => { setSent(false); setOpen(false); reset(); }, 1800);
  };

  const handleFaqSelect = (faq) => {
    setSelected(faq);
    setTimeout(() => sendToWhatsApp(faq.question), 600);
  };

  const handleCustomSend = () => { if (!customText.trim()) return; sendToWhatsApp(customText); };

  return (
    <>
      {/* ── Floating Button ──
          bottom-20 on mobile (above the 64px tab bar + 16px gap = 80px)
          bottom-6 on desktop (no tab bar)
      */}
      <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 flex flex-col items-end gap-3">
        <button
          onClick={() => { setOpen(o => !o); if (!open) reset(); }}
          className="group relative w-14 h-14 rounded-full shadow-2xl flex items-center justify-center
            bg-[#25D366] hover:bg-[#20BD5A] active:scale-95 transition-all duration-200"
          aria-label="Open WhatsApp chat"
        >
          {pulse && !open && (
            <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-40 animate-ping" />
          )}
          <span className={`transition-all duration-200 ${open ? "opacity-0 scale-75 rotate-90" : "opacity-100 scale-100 rotate-0"} absolute`}>
            <WhatsAppIcon size={28} />
          </span>
          <span className={`transition-all duration-200 text-white ${open ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-75 -rotate-90"} absolute`}>
            <CloseIcon />
          </span>
        </button>
      </div>

      {/* ── Chat Popup ──
          bottom-36 on mobile (above button which is at bottom-20, plus button height ~56px + gap)
          bottom-24 on desktop
      */}
      <div
        className={`fixed bottom-36 md:bottom-24 right-4 md:right-6 z-50 w-[340px] max-w-[calc(100vw-24px)]
          bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden
          transition-all duration-300 origin-bottom-right
          ${open ? "opacity-100 scale-100 translate-y-0 pointer-events-auto" : "opacity-0 scale-90 translate-y-4 pointer-events-none"}`}
      >
        {/* Header */}
        <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
            <WhatsAppIcon size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold text-sm leading-tight">Job Dew Support</div>
            <div className="text-green-300 text-xs flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
              Typically replies within minutes
            </div>
          </div>
          <button onClick={handleClose} className="text-white/60 hover:text-white transition-colors p-1">
            <CloseIcon />
          </button>
        </div>

        {/* Chat Body */}
        <div className="bg-[#ECE5DD] min-h-[200px] max-h-[300px] overflow-y-auto px-3 py-4 flex flex-col gap-3"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4c9bb' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
          }}
        >
          <div className="flex gap-2 items-end">
            <div className="w-7 h-7 rounded-full bg-[#075E54] flex items-center justify-center flex-shrink-0 mb-0.5">
              <WhatsAppIcon size={16} />
            </div>
            <div className="bg-white rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-sm max-w-[240px]">
              <p className="text-slate-800 text-sm leading-relaxed">👋 Hi! How can we help you today?</p>
              <p className="text-slate-500 text-xs mt-1">Choose a question below or write your own.</p>
            </div>
          </div>

          {!sent && (
            <div className="flex flex-col gap-2 pl-9">
              {faqs.length === 0 && <div className="text-xs text-slate-400 italic">Loading options…</div>}
              {faqs.map((faq) => (
                <button
                  key={faq.id}
                  onClick={() => handleFaqSelect(faq)}
                  disabled={selected !== null}
                  className={`text-left text-sm px-3.5 py-2 rounded-xl border transition-all duration-150
                    ${selected === faq
                      ? "bg-[#DCF8C6] border-[#25D366] text-slate-800 scale-[0.98]"
                      : "bg-white border-slate-200 text-slate-700 hover:border-[#25D366] hover:bg-[#F0FFF5] active:scale-[0.98]"
                    } shadow-sm`}
                >
                  {faq.question}
                </button>
              ))}
              <button
                onClick={() => setSelected("custom")}
                disabled={selected !== null && selected !== "custom"}
                className={`text-left text-sm px-3.5 py-2 rounded-xl border transition-all duration-150
                  ${selected === "custom"
                    ? "bg-[#DCF8C6] border-[#25D366] text-slate-800"
                    : "bg-white border-dashed border-slate-300 text-slate-500 hover:border-[#25D366] hover:text-slate-700 hover:bg-[#F0FFF5]"
                  } shadow-sm`}
              >
                ✏️ Other question…
              </button>
            </div>
          )}

          {selected === "custom" && !sent && (
            <div className="pl-9 mt-1">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <textarea
                  ref={inputRef}
                  value={customText}
                  onChange={e => setCustomText(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleCustomSend(); } }}
                  placeholder="Type your question here…"
                  rows={3}
                  className="w-full px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 resize-none outline-none"
                />
                <div className="flex justify-end px-2 pb-2">
                  <button
                    onClick={handleCustomSend}
                    disabled={!customText.trim()}
                    className="flex items-center gap-1.5 bg-[#25D366] disabled:bg-slate-200 disabled:text-slate-400
                      text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:bg-[#20BD5A] active:scale-95"
                  >
                    <SendIcon />
                    Send on WhatsApp
                  </button>
                </div>
              </div>
            </div>
          )}

          {sent && (
            <div className="flex gap-2 items-end pl-9">
              <div className="bg-[#DCF8C6] rounded-2xl rounded-br-sm px-3.5 py-2.5 shadow-sm max-w-[240px] ml-auto">
                <p className="text-slate-800 text-sm">✅ Opening WhatsApp…</p>
                <p className="text-slate-500 text-xs mt-0.5">You'll continue the chat there</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white px-4 py-2 border-t border-slate-100 flex items-center justify-center gap-1.5">
          <WhatsAppIcon size={14} />
          <span className="text-xs text-slate-400">Powered by WhatsApp</span>
        </div>
      </div>
    </>
  );
}